import { colors } from '@/theme/foundations/colors';
import { formulaTokens, type FormulaIssue, type Token } from './formulaEngine';

export type SegmentKind =
  | 'field'
  | 'string'
  | 'number'
  | 'function'
  | 'operator'
  | 'plain';

export interface FormulaSegment {
  // Offset into the formula; a stable key while the text changes under it.
  start: number;
  text: string;
  kind: SegmentKind;
  // Set when this piece is what an inline error points at.
  error?: string;
}

interface Mark {
  start: number;
  end: number;
  kind: SegmentKind;
  error?: string;
}

// A field keeps its braces and a string its quotes, so both are wider than their text.
const tokenEnd = (token: Token, formula: string) => {
  if (token.kind === 'field' || token.kind === 'string') {
    const closer = token.kind === 'field' ? '}' : '"';
    const closed = formula.indexOf(closer, token.start + 1);
    return closed === -1 ? formula.length : closed + 1;
  }
  return token.start + token.text.length;
};

const kindOf = (token: Token, next?: Token): SegmentKind => {
  if (token.kind === 'field') return 'field';
  if (token.kind === 'string') return 'string';
  if (token.kind === 'number') return 'number';
  if (token.kind === 'operator') return 'operator';
  if (token.kind === 'name' && next?.kind === 'open') return 'function';
  return 'plain';
};

// The formula split for colouring: every character lands in exactly one segment.
export const formulaSegments = (
  formula: string,
  issues: FormulaIssue[]
): FormulaSegment[] => {
  const tokens = formulaTokens(formula);
  const marks: Mark[] = tokens.map((token, index) => ({
    start: token.start,
    end: tokenEnd(token, formula),
    kind: kindOf(token, tokens[index + 1]),
  }));

  // An issue either marks a token (unknown field) or stands alone (bad operator).
  issues.forEach((issue) => {
    if (issue.start < 0) return;
    const onToken = marks.find((mark) => mark.start === issue.start);
    if (onToken) onToken.error = issue.message;
    else
      marks.push({
        start: issue.start,
        end: issue.start + (issue.text.length || 1),
        kind: 'plain',
        error: issue.message,
      });
  });

  const segments: FormulaSegment[] = [];
  let position = 0;
  marks
    .sort((a, b) => a.start - b.start)
    .forEach((mark) => {
      if (mark.start < position) return;
      if (mark.start > position)
        segments.push({
          start: position,
          text: formula.slice(position, mark.start),
          kind: 'plain',
        });
      segments.push({
        start: mark.start,
        text: formula.slice(mark.start, mark.end),
        kind: mark.kind,
        error: mark.error,
      });
      position = mark.end;
    });
  if (position < formula.length)
    segments.push({
      start: position,
      text: formula.slice(position),
      kind: 'plain',
    });
  return segments;
};

// The design's four text styles: fields dark, operators green, values purple,
// functions red. Strings and numbers are both "value", so they share a colour.
export const SEGMENT_COLOR: Record<SegmentKind, string> = {
  field: colors['secondary-900'],
  string: colors['primary-500'],
  number: colors['primary-500'],
  function: colors['pastel-1-dark'],
  operator: colors['success-200'],
  plain: colors['secondary-700'],
};
